import type { BaseQueryFn, FetchArgs, FetchBaseQueryError, FetchBaseQueryMeta } from '@reduxjs/toolkit/query';
import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { isDefined, isNonEmpty } from '@sgme/fp';
import { v4 } from 'uuid';

import { logger } from '@/components/SgwtBootstrap/logger.ts';
import { getConfig } from '@/config/config.ts';
import { sgwtConnect } from '@/sgwt/sgwtConnect.ts';

const rawBaseQuery = fetchBaseQuery({
  baseUrl: `${getConfig().app.apiUrl}api`,
  prepareHeaders,
});

const loggedBaseQuery: BaseQueryFn<string | FetchArgs, unknown, FetchBaseQueryError, {}, FetchBaseQueryMeta> = async (
  args,
  api,
  extraOptions,
) => {
  try {
    logHttpRequest(args);
  } catch {}
  return rawBaseQuery(args, api, extraOptions);
};

export const webapi = createApi({
  reducerPath: 'webapi',
  tagTypes: ['getCurrentUser'],

  baseQuery: loggedBaseQuery,

  endpoints: () => ({}),
});

function logHttpRequest(args: string | FetchArgs) {
  if (typeof args === 'string') {
    logger.logInformation('Http request GET {url_s}', JSON.stringify(args));
  } else {
    const { url, body, method, params } = args;
    logger.logInformation(
      'Http request {method_s} {url_s} {query_params_s} {body_s}',
      method,
      url,
      JSON.stringify(params),
      JSON.stringify(body),
    );
  }
}

export function prepareHeaders(headers: Headers): Headers {
  headers.set('Application', 'FX');
  headers.set('Front-Version', getConfig().frontVersion);

  const authorizationHeader = sgwtConnect.getAuthorizationHeader();

  if (isDefined(authorizationHeader)) {
    headers.set('Authorization', authorizationHeader);
  }

  const fakeUser = getConfig().userEmail;

  if (isDefined(fakeUser) && isNonEmpty(fakeUser)) {
    // check env
    headers.set('X-Fake-Authentication', fakeUser);
  }

  if (!headers.has('Accept')) {
    headers.set('Accept', 'application/json');
  }

  headers.set('Content-Type', 'application/json');

  if (!headers.has('X-Correlation-Id')) {
    headers.set('X-Correlation-ID', v4());
  }

  return headers;
}

// ████████╗██╗   ██╗██████╗ ███████╗███████╗
// ╚══██╔══╝╚██╗ ██╔╝██╔══██╗██╔════╝██╔════╝
//    ██║    ╚████╔╝ ██████╔╝█████╗  ███████╗
//    ██║     ╚██╔╝  ██╔═══╝ ██╔══╝  ╚════██║
//    ██║      ██║   ██║     ███████╗███████║
//    ╚═╝      ╚═╝   ╚═╝     ╚══════╝╚══════╝

type WebApiType = typeof webapi;

type BaseQueryType = Parameters<WebApiType['injectEndpoints']>;

export type WebapiEndpointBuilder = Parameters<BaseQueryType[0]['endpoints']>[0];
